import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Bars } from "react-loader-spinner";
import axiosInstance from "../../Shared/axiosInstance.js"; // استيراد axiosInstance المركزي

export default function SalesCustomerSummary() {
  const { id } = useParams(); // CustomerId
  const navigate = useNavigate();

  const [sales, setSales] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [loading, setLoading] = useState(false);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Fetch Sales with Pagination
  const fetchSales = async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `/Sale/by-customer/${id}/summary?pageNumber=${page}&pageSize=${size}`
      );

      setSales(data.data || []);
      setTotalCount(data.totalCount || 0);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(data.currentPage || 1);

      // Set customer name from first sale
      if (data.data && data.data.length > 0) {
        setCustomerName(data.data[0].customerName);
      }
    } catch (err) {
      console.error("Error fetching customer sales:", err);
      Swal.fire("خطأ", "فشل في تحميل مبيعات العميل", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales(1, pageSize);
  }, [id]);

  // Pagination Handlers
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    fetchSales(newPage, pageSize);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    fetchSales(1, newSize);
  };

  // Loading State
  if (loading && sales.length === 0) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <Bars height="80" width="80" color="#2563eb" ariaLabel="loading" />
        <p className="mt-3" style={{ color: "#64748b", fontSize: "18px" }}>
          جاري تحميل المبيعات...
        </p>
      </div>
    );
  }

  return (
    <div className="container py-5" style={{ maxWidth: "1400px" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
        <div>
          <h2
            style={{
              color: "#2563eb",
              fontWeight: 700,
              fontSize: 28,
              marginBottom: 4,
            }}
          >
            مبيعات العميل {customerName && `- ${customerName}`}
          </h2>
          <small style={{ color: "#64748b", fontSize: 14 }}>
            عرض {sales.length} من {totalCount} عملية بيع
          </small>
        </div>
        <button
          onClick={() => navigate("/customers")}
          style={{
            background: "#fff",
            color: "#475569",
            padding: "10px 20px",
            borderRadius: 6,
            border: "1px solid #bfdbfe",
            fontWeight: 600,
            fontSize: 15,
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
          onMouseOver={(e) => (e.target.style.background = "#f8fafc")}
          onMouseOut={(e) => (e.target.style.background = "#fff")}
        >
          <i className="bi bi-arrow-left me-1"></i> العودة للعملاء
        </button>
      </div>

      {/* Info Bar + Page Size Selector */}
      {sales.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span style={{ color: "#64748b", fontSize: 14 }}>
            عرض {((currentPage - 1) * pageSize) + 1} إلى{" "}
            {Math.min(currentPage * pageSize, totalCount)} من {totalCount} عملية بيع
          </span>
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            disabled={loading}
            style={{
              padding: "8px 12px",
              border: "1px solid #bfdbfe",
              borderRadius: 6,
              fontSize: 14,
              color: "#475569",
              fontWeight: 600,
              outline: "none",
              cursor: loading ? "not-allowed" : "pointer",
              background: "#fff",
              opacity: loading ? 0.6 : 1,
            }}
          >
            <option value="5">5 لكل صفحة</option>
            <option value="10">10 لكل صفحة</option>
            <option value="20">20 لكل صفحة</option>
            <option value="50">50 لكل صفحة</option>
          </select>
        </div>
      )}

      {/* Sales Table */}
      {sales.length > 0 ? (
        <>
          <div className="table-responsive">
            <table
              className="table mb-0"
              style={{
                border: "1px solid #dbeafe",
                borderRadius: 8,
                overflow: "hidden",
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
                textAlign: "center",
                opacity: loading ? 0.6 : 1,
                transition: "opacity 0.2s",
              }}
            >
              <thead style={{ background: "#dbeafe" }}>
                <tr>
                  {[
                    "#",
                    "العميل",
                    "التاريخ",
                    "الإجمالي",
                    "المدفوع",
                    "المتبقي",
                    "الحالة",
                    "الإجراءات",
                  ].map((th, i) => (
                    <th key={i} style={thStyle}>
                      {th}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sales.map((s, index) => (
                  <tr
                    key={s.id}
                    style={{
                      background: index % 2 === 0 ? "#f8fafc" : "#ffffff",
                    }}
                  >
                    <td style={tdStyle}>
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td
                      style={{ ...tdStyle, fontWeight: 700, color: "#2563eb" }}
                    >
                      {s.customerName}
                    </td>
                    <td style={tdStyle}>
                      {new Date(s.saleDate).toLocaleDateString()}
                    </td>
                    <td
                      style={{ ...tdStyle, fontWeight: 700, color: "#22c55e" }}
                    >
                      {s.totalAmount.toFixed(2)}
                    </td>
                    <td
                      style={{ ...tdStyle, fontWeight: 600, color: "#2563eb" }}
                    >
                      {s.paidAmount.toFixed(2)}
                    </td>
                    <td
                      style={{ ...tdStyle, fontWeight: 700, color: "#dc2626" }}
                    >
                      {s.remainingAmount.toFixed(2)}
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          background:
                            s.paymentStatus === "Paid"
                              ? "#bbf7d0"
                              : s.paymentStatus === "PartiallyPaid"
                              ? "#fef3c7"
                              : "#fecaca",
                          color:
                            s.paymentStatus === "Paid"
                              ? "#14532d"
                              : s.paymentStatus === "PartiallyPaid"
                              ? "#92400e"
                              : "#991b1b",
                          padding: "4px 10px",
                          borderRadius: 4,
                          fontSize: 17,
                          fontWeight: 600,
                        }}
                      >
                        {s.paymentStatus === "PartiallyPaid"
                          ? "مدفوع جزئيًا"
                          : s.paymentStatus === "Paid"
                          ? "مدفوع"
                          : s.paymentStatus}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div className="d-flex gap-2 justify-content-center flex-wrap">
                        <button
                          style={actionBtnStyle("#2563eb")}
                          onClick={() => navigate(`/sales/${s.id}`)}
                        >
                          <i className="bi bi-eye me-1"></i> التفاصيل
                        </button>
                        <button
                          style={actionBtnStyle("#22c55e")}
                          onClick={() =>
                            navigate(`/sales/${s.id}/add-payment-sale`)
                          }
                        >
                          <i className="bi bi-plus-circle me-1"></i> إضافة دفعة
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div style={{ color: "#64748b", fontSize: 14 }}>
                الصفحة {currentPage} من {totalPages}
              </div>
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      style={paginationBtnStyle}
                    >
                      السابق
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => {
                    const pageNum = i + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                    ) {
                      return (
                        <li key={pageNum}>
                          <button
                            onClick={() => handlePageChange(pageNum)}
                            style={{
                              ...paginationBtnStyle,
                              background:
                                currentPage === pageNum ? "#2563eb" : "#fff",
                              color:
                                currentPage === pageNum ? "#fff" : "#475569",
                            }}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    } else if (
                      pageNum === currentPage - 3 ||
                      pageNum === currentPage + 3
                    ) {
                      return (
                        <li key={pageNum}>
                          <span
                            style={{
                              ...paginationBtnStyle,
                              cursor: "default",
                              border: "none",
                            }}
                          >
                            ...
                          </span>
                        </li>
                      );
                    }
                    return null;
                  })}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      style={paginationBtnStyle}
                    >
                      التالي
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            background: "#fff",
            border: "1px solid #dbeafe",
            borderRadius: 8,
            padding: "60px 20px",
            textAlign: "center",
            boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)",
          }}
        >
          <i
            className="bi bi-cart-check"
            style={{ fontSize: 64, color: "#94a3b8", marginBottom: 20 }}
          ></i>
          <h4 style={{ color: "#475569", fontWeight: 600, marginBottom: 8 }}>
            لا توجد مبيعات
          </h4>
          <p style={{ color: "#64748b", fontSize: 15, marginBottom: 0 }}>
            هذا العميل لا يحتوي على أي سجلات مبيعات حتى الآن
          </p>
        </div>
      )}
    </div>
  );
}

// Styles
const thStyle = {
  color: "#1e40af",
  fontSize: 14,
  padding: "12px 8px",
  fontWeight: 600,
  border: "1px solid #bfdbfe",
};
const tdStyle = {
  padding: "12px 8px",
  border: "1px solid #e0e7ff",
  fontSize: 19,
  fontWeight: 600,
  color: "#475569",
};
const actionBtnStyle = (color) => ({
  background: color,
  color: "#fff",
  padding: "6px 12px",
  borderRadius: 6,
  border: "none",
  fontWeight: 600,
  fontSize: 17,
  cursor: "pointer",
  transition: "all 0.3s ease",
});
const paginationBtnStyle = {
  padding: "8px 14px",
  margin: "0 3px",
  border: "1px solid #bfdbfe",
  borderRadius: 6,
  fontSize: 14,
  cursor: "pointer",
  transition: "all 0.2s ease",
};
